import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fmt } from "@/api";
import { useApp } from "@/context/AppContext";
import ProductCard from "@/components/ProductCard";
import { Heart, Truck, ShieldCheck, RotateCcw, Star, Minus, Plus } from "lucide-react";

export default function ProductDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [related, setRelated] = useState([]);
  const [imgIdx, setImgIdx] = useState(0);
  const [variantId, setVariantId] = useState("");
  const { addToCart, toggleWishlist, wishlistIds } = useApp();
  const nav = useNavigate();

  useEffect(() => {
    setImgIdx(0); setQty(1); setVariantId("");
    api.get(`/products/${slug}`).then(({data}) => {
      setP(data);
      if (data.variants && data.variants.length) {
        const first = data.variants.find(v=>v.is_active && v.stock>0) || data.variants[0];
        setVariantId(first.id);
      }
      api.get(`/products?category=${data.category}&limit=8`).then(r => setRelated(r.data.filter(x=>x.slug!==slug).slice(0,4)));
    });
  }, [slug]);

  if (!p) return <div className="max-w-7xl mx-auto px-4 py-20 text-center text-muted-foreground">Loading...</div>;
  const activeVar = (p.variants||[]).find(v=>v.id===variantId);
  const dispPrice = activeVar ? activeVar.price : p.price;
  const dispMrp = activeVar ? (activeVar.mrp || activeVar.price) : p.mrp;
  const dispStock = activeVar ? activeVar.stock : p.stock;
  const disc = dispMrp > dispPrice ? Math.round(((dispMrp - dispPrice)/dispMrp)*100) : 0;
  const inWl = wishlistIds.includes(p.id);
  const images = p.images && p.images.length ? p.images : [p.image];
  const add = () => {
    if ((p.variants||[]).length && !variantId) { return; }
    const item = { product_id: p.id, name: p.name, price: dispPrice, image: p.image, slug: p.slug };
    if (activeVar) {
      item.variant_id = activeVar.id;
      item.variant_name = activeVar.name || `${activeVar.size} / ${activeVar.color}`.replace(/^ \/ | \/ $/,"");
      item.name = `${p.name} — ${item.variant_name}`;
    }
    addToCart(item, qty);
  };
  const buyNow = () => { add(); nav("/checkout"); };

  return (
    <div className="max-w-7xl mx-auto px-4 py-10">
      <div className="text-xs text-muted-foreground mb-6"><Link to="/" className="hover:underline">Home</Link> / <Link to={`/category/${p.category}`} className="hover:underline capitalize">{p.category.replace(/-/g," ")}</Link> / <span className="text-foreground">{p.name}</span></div>

      <div className="grid md:grid-cols-2 gap-10">
        <div>
          <div className="rounded-2xl overflow-hidden bg-muted aspect-square border border-border">
            <img src={images[imgIdx]} alt={p.name} className="w-full h-full object-cover"/>
          </div>
          {images.length > 1 && (
            <div className="mt-3 flex gap-2">
              {images.map((im,i)=>(
                <button key={i} onClick={()=>setImgIdx(i)} className={`w-16 h-16 rounded-lg overflow-hidden border-2 ${imgIdx===i?"border-primary":"border-transparent"}`}><img src={im} className="w-full h-full object-cover"/></button>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="section-label">{p.brand}</div>
          <h1 className="font-serif text-3xl md:text-4xl mt-2">{p.name}</h1>
          <div className="mt-3 flex items-center gap-3 text-sm">
            <div className="flex items-center gap-1"><Star className="w-4 h-4 fill-secondary text-secondary"/><span className="font-semibold">{p.rating}</span><span className="text-muted-foreground">({p.reviews} reviews)</span></div>
            {p.weight && <span className="text-muted-foreground">· {p.weight}</span>}
          </div>

          <div className="mt-5 flex items-baseline gap-3">
            <span className="text-3xl font-semibold text-primary">{fmt(dispPrice)}</span>
            {dispMrp > dispPrice && <span className="line-through text-muted-foreground">{fmt(dispMrp)}</span>}
            {disc > 0 && <span className="bg-accent text-white text-xs font-bold px-2 py-1 rounded-full">-{disc}% OFF</span>}
          </div>
          <div className="text-xs text-muted-foreground mt-1">Inclusive of all taxes</div>

          {p.variants && p.variants.length > 0 && (
            <div className="mt-5" data-testid="variants-panel">
              <div className="section-label mb-2">Choose Variant</div>
              <div className="flex flex-wrap gap-2">
                {p.variants.filter(v=>v.is_active !== false).map(v => {
                  const label = v.name || [v.size, v.color].filter(Boolean).join(" / ");
                  const out = v.stock <= 0;
                  return (
                    <button key={v.id} data-testid={`variant-${v.id}`} onClick={()=>!out && setVariantId(v.id)}
                      disabled={out}
                      className={`px-4 py-2 rounded-lg border text-sm transition-all ${variantId===v.id?"border-primary bg-primary/5 font-semibold text-primary":"border-border hover:border-primary/50"} ${out?"line-through opacity-50 cursor-not-allowed":""}`}>
                      {label}
                      {v.mrp>v.price && <span className="ml-2 text-xs text-muted-foreground">₹{v.price}</span>}
                    </button>
                  );
                })}
              </div>
              {activeVar && activeVar.stock > 0 && activeVar.stock < 10 && <div className="text-xs text-accent mt-2">Only {activeVar.stock} left in this variant</div>}
              {activeVar && activeVar.sku && <div className="text-[10px] uppercase text-muted-foreground mt-1">SKU: {activeVar.sku}</div>}
            </div>
          )}

          {/* Payment benefits */}
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
            <div className="p-2 rounded-lg bg-emerald-50 border border-emerald-100"><b>🚚 FREE Shipping</b> on orders ₹399+</div>
            <div className="p-2 rounded-lg bg-primary/5 border border-primary/10"><b>💳 5% OFF</b> on online payment</div>
            <div className="p-2 rounded-lg bg-secondary/20 border border-secondary/30"><b>💵 COD</b> available (+₹50)</div>
          </div>

          <p className="mt-5 leading-relaxed text-foreground/80">{p.description}</p>

          {p.whats_included && p.whats_included.length > 0 && (
            <div className="mt-5">
              <div className="section-label">What's Included</div>
              <ul className="mt-2 grid grid-cols-2 gap-1 text-sm">
                {p.whats_included.map((x,i)=>(<li key={i} className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary"/>{x}</li>))}
              </ul>
            </div>
          )}

          <div className="mt-7 flex items-center gap-4">
            <div className="flex items-center border border-border rounded-full">
              <button data-testid="qty-dec" onClick={()=>setQty(Math.max(1, qty-1))} className="w-10 h-10 grid place-items-center"><Minus className="w-4 h-4"/></button>
              <span className="w-10 text-center font-medium">{qty}</span>
              <button data-testid="qty-inc" onClick={()=>setQty(qty+1)} className="w-10 h-10 grid place-items-center"><Plus className="w-4 h-4"/></button>
            </div>
            <div className={`text-xs font-medium ${p.stock>10?"text-emerald-700":"text-accent"}`}>{p.stock>0 ? `${p.stock} in stock` : "Out of stock"}</div>
          </div>

          <div className="mt-5 flex flex-wrap gap-3">
            <button data-testid="add-to-cart-btn" disabled={p.stock<=0} onClick={add} className="pk-btn-secondary flex-1 min-w-[180px] disabled:opacity-50">Add to Cart</button>
            <button data-testid="buy-now-btn" disabled={p.stock<=0} onClick={buyNow} className="pk-btn-primary flex-1 min-w-[180px] disabled:opacity-50">Buy Now</button>
            <button data-testid="wishlist-btn" onClick={()=>toggleWishlist(p.id)} className="w-12 h-12 rounded-full border border-border grid place-items-center hover:border-primary">
              <Heart className={`w-5 h-5 ${inWl?"fill-primary text-primary":""}`}/>
            </button>
          </div>

          <div className="mt-8 grid grid-cols-3 gap-3 text-xs">
            <div className="flex flex-col items-center text-center gap-1 p-3 rounded-lg border border-border"><Truck className="w-4 h-4 text-primary"/>Fast Delivery</div>
            <div className="flex flex-col items-center text-center gap-1 p-3 rounded-lg border border-border"><ShieldCheck className="w-4 h-4 text-primary"/>Authentic Product</div>
            <div className="flex flex-col items-center text-center gap-1 p-3 rounded-lg border border-border"><RotateCcw className="w-4 h-4 text-primary"/>Easy Returns</div>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <div className="mt-16">
          <h2 className="font-serif text-2xl md:text-3xl mb-6">You may also like</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {related.map(r => <ProductCard key={r.id} p={r}/>)}
          </div>
        </div>
      )}
    </div>
  );
}
